import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function normalizeQuery(q) {
  return (q || '').trim().toLocaleLowerCase('tr-TR');
}

function matchItem(item, query) {
  if (!query) return true;
  const hay = `${item.label} ${item.group || ''}`.toLocaleLowerCase('tr-TR');
  return hay.includes(query);
}

function SearchIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
    </svg>
  );
}

export default function AdminHeaderSearch({ items = [], onSelect, disabled = false }) {
  const inputId = useId();
  const inputRef = useRef(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const groupedResults = useMemo(() => {
    const q = normalizeQuery(query);
    const filtered = q
      ? items.filter((item) => matchItem(item, q))
      : items;
    const map = new Map();
    for (const item of filtered) {
      const group = item.group || 'Diğer';
      if (!map.has(group)) map.set(group, []);
      map.get(group).push(item);
    }
    return [...map.entries()];
  }, [items, query]);

  const flatResults = useMemo(
    () => groupedResults.flatMap(([, groupItems]) => groupItems),
    [groupedResults],
  );

  const close = useCallback(() => {
    setPaletteOpen(false);
    setQuery('');
    setActiveIndex(0);
  }, []);

  const open = useCallback(() => {
    if (disabled) return;
    setPaletteOpen(true);
  }, [disabled]);

  const pick = useCallback(
    (item) => {
      if (!item) return;
      onSelect?.(item);
      close();
    },
    [onSelect, close],
  );

  useEffect(() => {
    if (!paletteOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => {
      document.body.style.overflow = prev;
      window.clearTimeout(t);
    };
  }, [paletteOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (paletteOpen) close();
        else open();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [paletteOpen, open, close]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, flatResults.length]);

  const onInputKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(0, flatResults.length - 1)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (flatResults[activeIndex]) pick(flatResults[activeIndex]);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  let flatOffset = 0;

  const palette = paletteOpen && !disabled
    ? createPortal(
        <div
          className="admin-spotlight-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div
            className="admin-spotlight-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Ara"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="admin-spotlight-input-row">
              <SearchIcon className="admin-spotlight-input-icon" />
              <input
                ref={inputRef}
                id={inputId}
                type="search"
                className="admin-spotlight-input"
                value={query}
                placeholder="Ara"
                autoComplete="off"
                spellCheck={false}
                aria-autocomplete="list"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onInputKeyDown}
              />
              {query ? (
                <button
                  type="button"
                  className="admin-spotlight-clear"
                  aria-label="Temizle"
                  onClick={() => {
                    setQuery('');
                    inputRef.current?.focus();
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm3.54 12.46a.75.75 0 01-1.06 1.06L12 13.06l-2.48 2.46a.75.75 0 11-1.06-1.06L10.94 12 8.46 9.54a.75.75 0 111.06-1.06L12 10.94l2.48-2.46a.75.75 0 111.06 1.06L13.06 12l2.48 2.46z" />
                  </svg>
                </button>
              ) : null}
            </div>

            <div className="admin-spotlight-results" role="listbox">
              {flatResults.length === 0 ? (
                <p className="admin-spotlight-empty">Sonuç bulunamadı</p>
              ) : (
                groupedResults.map(([group, groupItems]) => {
                  const section = (
                    <section key={group} className="admin-spotlight-section">
                      <h3 className="admin-spotlight-section-title">{group}</h3>
                      <ul className="admin-spotlight-section-list">
                        {groupItems.map((item) => {
                          const index = flatOffset;
                          flatOffset += 1;
                          const active = index === activeIndex;
                          return (
                            <li key={item.id}>
                              <button
                                type="button"
                                role="option"
                                aria-selected={active}
                                className={`admin-spotlight-row${active ? ' is-active' : ''}`}
                                onMouseEnter={() => setActiveIndex(index)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => pick(item)}
                              >
                                {item.icon ? (
                                  <span className="admin-spotlight-row-icon" aria-hidden>{item.icon}</span>
                                ) : null}
                                <span className="admin-spotlight-row-label">{item.label}</span>
                                <span className="admin-spotlight-row-hint" aria-hidden>↵</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  );
                  return section;
                })
              )}
            </div>

            <footer className="admin-spotlight-footer">
              <span><kbd>↵</kbd> Aç</span>
              <span><kbd>↑</kbd><kbd>↓</kbd> Gezin</span>
              <span><kbd>esc</kbd> Kapat</span>
            </footer>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        type="button"
        className="admin-spotlight-trigger"
        disabled={disabled}
        onClick={open}
        aria-label="Ara (⌘K)"
      >
        <SearchIcon className="admin-spotlight-trigger-icon" />
        <span className="admin-spotlight-trigger-label">Ara</span>
        <kbd className="admin-spotlight-trigger-kbd" aria-hidden>⌘K</kbd>
      </button>
      {palette}
    </>
  );
}
